import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, Target, Zap } from 'lucide-react';
import { useHabits } from '../contexts/HabitsContext';
import { predefinedHabits, PredefinedHabit } from '../data/predefinedHabits';
import styles from './CreateHabitModal.module.css';

interface CreateHabitModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreateHabitModal: React.FC<CreateHabitModalProps> = ({ isOpen, onClose }) => {
  const { createHabit } = useHabits();
  const [activeTab, setActiveTab] = useState<'predefined' | 'custom'>('predefined');
  const [customHabit, setCustomHabit] = useState({
    name: '',
    description: '',
    category: 'custom' as const,
    duration: 5,
    difficulty: 'facil' as const,
    hasCustomValue: false,
    customValueLabel: '',
    customValueUnit: '',
    customValueType: 'number' as const
  });
  const [isCreating, setIsCreating] = useState(false);
  const handleCreatePredefined = async (habit: PredefinedHabit) => {
    setIsCreating(true);
    try {
      const habitData: any = {
        name: habit.name,
        description: habit.description,
        category: habit.category,
        duration: habit.defaultDuration,
        difficulty: habit.difficulty,
        isDefault: true,
        hasCustomValue: habit.hasCustomValue || false
      };

      // Só adicionar campos customizados se eles existirem e não forem undefined
      if (habit.hasCustomValue) {
        if (habit.customValueLabel) {
          habitData.customValueLabel = habit.customValueLabel;
        }
        if (habit.customValueUnit) {
          habitData.customValueUnit = habit.customValueUnit;
        }
        if (habit.customValueType) {
          habitData.customValueType = habit.customValueType;
        }
      }

      await createHabit(habitData);
      onClose();
    } catch (error) {
      console.error('Erro ao criar hábito:', error);
    } finally {
      setIsCreating(false);
    }
  };  const handleCreateCustom = async () => {
    if (!customHabit.name.trim() || customHabit.duration <= 0) return;
    
    setIsCreating(true);
    try {
      const habitData: any = {
        name: customHabit.name.trim(),
        description: customHabit.description.trim() || '',
        category: customHabit.category,
        duration: customHabit.duration,
        difficulty: customHabit.difficulty,
        isDefault: false,
        hasCustomValue: customHabit.hasCustomValue
      };

      // Só adicionar campos customizados se eles existirem e hasCustomValue for true
      if (customHabit.hasCustomValue) {
        if (customHabit.customValueLabel && customHabit.customValueLabel.trim()) {
          habitData.customValueLabel = customHabit.customValueLabel.trim();
        }
        if (customHabit.customValueUnit && customHabit.customValueUnit.trim()) {
          habitData.customValueUnit = customHabit.customValueUnit.trim();
        }
        habitData.customValueType = customHabit.customValueType || 'number';
      }

      await createHabit(habitData);
      setCustomHabit({
        name: '',
        description: '',
        category: 'custom',
        duration: 5,
        difficulty: 'facil',
        hasCustomValue: false,
        customValueLabel: '',
        customValueUnit: '',
        customValueType: 'number'
      });
      onClose();
    } catch (error) {
      console.error('Erro ao criar hábito:', error);
    } finally {
      setIsCreating(false);
    }
  };
  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'facil': return styles.difficultyFacil;
      case 'medio': return styles.difficultyMedio;
      case 'dificil': return styles.difficultyDificil;
      default: return styles.difficultyFacil;
    }
  };

  const getDifficultyIcon = (difficulty: string) => {
    switch (difficulty) {
      case 'facil': return <Zap className="w-4 h-4" />;
      case 'medio': return <Target className="w-4 h-4" />;
      case 'dificil': return <Target className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  if (!isOpen) return null;
  return (
    <AnimatePresence>
      <div className={styles.backdrop}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className={styles.modalContent}
        >
          {/* Header */}
          <div className={styles.header}>
            <h2 className={styles.title}>Adicionar Novo Hábito</h2>
            <button
              onClick={onClose}
              className={styles.closeButton}
            >
              <X className="w-5 h-5" />
            </button>
          </div>          {/* Tabs */}
          <div className={styles.tabsContainer}>
            <button
              onClick={() => setActiveTab('predefined')}
              className={`${styles.tab} ${
                activeTab === 'predefined' ? styles.tabActive : styles.tabInactive
              }`}
            >
              Hábitos Sugeridos
            </button>
            <button
              onClick={() => setActiveTab('custom')}
              className={`${styles.tab} ${
                activeTab === 'custom' ? styles.tabActive : styles.tabInactive
              }`}
            >
              Criar Personalizado
            </button>
          </div>

          {/* Content */}
          <div className={styles.content}>
            {activeTab === 'predefined' ? (
              <div className={styles.habitsList}>
                <p className={styles.description}>
                  Escolha um dos hábitos pré-definidos, especialmente criados para iniciantes:
                </p>
                
                {predefinedHabits.map((habit, index) => (
                  <motion.div
                    key={habit.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={styles.habitCard}
                  >
                    <div className={styles.habitCardHeader}>
                      <div className={styles.habitInfo}>
                        <h3 className={styles.habitName}>{habit.name}</h3>
                        <p className={styles.habitDescription}>{habit.description}</p>
                        
                        <div className={styles.habitMeta}>
                          <div className={styles.metaItem}>
                            <Clock className={styles.metaIcon} />
                            {habit.defaultDuration} min
                          </div>
                          <div className={`${styles.difficultyBadge} ${getDifficultyColor(habit.difficulty)}`}>
                            {getDifficultyIcon(habit.difficulty)}
                            <span className={styles.difficultyText}>{habit.difficulty}</span>
                          </div>
                        </div>                      </div>
                      <button
                        onClick={() => handleCreatePredefined(habit)}
                        disabled={isCreating}
                        className={styles.addButton}
                      >
                        Adicionar
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>            ) : (
              <div className={styles.form}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Nome do hábito *
                  </label>
                  <input
                    type="text"
                    value={customHabit.name}
                    onChange={(e) => setCustomHabit(prev => ({ ...prev, name: e.target.value }))}
                    placeholder=" O que você fará e quando? (Ex: Ler 10min antes de dormir)"
                    className={styles.input}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Descrição (opcional)
                  </label>
                  <textarea
                    value={customHabit.description}
                    onChange={(e) => setCustomHabit(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Ex: Qual a ação específica e qual seu gatilho? (Ex: Quando deitar na cama, lerei 10 páginas de um livro)"
                    rows={3}
                    className={styles.textarea}
                  />
                </div>

                <div className={styles.gridCols2}>                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Duração (minutos)
                    </label>                    <div className={styles.durationInputContainer}>
                      <button
                        type="button"
                        className={styles.durationButton}
                        onClick={() => setCustomHabit(prev => ({ 
                          ...prev, 
                          duration: Math.max(1, (prev.duration || 1) - 1) 
                        }))}
                        disabled={(customHabit.duration || 1) <= 1}
                      >
                        -
                      </button>
                      <input
                        type="number"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        min="1"
                        max="120"
                        value={customHabit.duration === 0 ? '' : customHabit.duration || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            // Permite campo vazio
                            setCustomHabit(prev => ({ ...prev, duration: 0 }));
                          } else {
                            const num = parseInt(value);
                            if (!isNaN(num) && num >= 0 && num <= 120) {
                              setCustomHabit(prev => ({ ...prev, duration: num }));
                            }
                          }
                        }}
                        onBlur={() => {
                          // Só aplica valor padrão se estiver vazio e o usuário sair do campo
                          if (!customHabit.duration || customHabit.duration === 0) {
                            setCustomHabit(prev => ({ ...prev, duration: 5 }));
                          }
                        }}
                        className={styles.durationInput}
                        placeholder="Digite a duração"
                      />
                      <button
                        type="button"
                        className={styles.durationButton}
                        onClick={() => setCustomHabit(prev => ({ 
                          ...prev, 
                          duration: Math.min(120, (prev.duration || 0) + 1) 
                        }))}
                        disabled={(customHabit.duration || 0) >= 120}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Dificuldade
                    </label>
                    <select
                      value={customHabit.difficulty}
                      onChange={(e) => setCustomHabit(prev => ({ ...prev, difficulty: e.target.value as any }))}
                      className={styles.select}
                    >
                      <option value="facil">Fácil</option>
                      <option value="medio">Médio</option>
                      <option value="dificil">Difícil</option>
                    </select>
                  </div>
                </div>

                <div className={styles.tip}>
                  <h4 className={styles.tipTitle}>💡 Dica dos Hábitos Atômicos</h4>
                  <p className={styles.tipText}>
                    Comece pequeno! É melhor fazer 2 minutos todos os dias do que 30 minutos uma vez por semana.
                    Você sempre pode aumentar a duração depois que o hábito se tornar automático.
                  </p>
                </div>                <button
                  onClick={handleCreateCustom}
                  disabled={!customHabit.name.trim() || customHabit.duration <= 0 || isCreating}
                  className={styles.primaryButton}
                >
                  {isCreating ? 'Criando...' : 'Criar Hábito'}
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default CreateHabitModal;
